import { Supply } from '@proc7ts/supply';
import { nodeWindow } from '@frontmeans/dom-primitives';
import { cxSingle, cxRecent, cxUnavailable, cxScoped, CxGlobals } from '@proc7ts/context-values';

/**
 * Creates a render schedule mapped from another one.
 *
 * The mapped schedule may support a render execution of another type.
 *
 * @typeParam TFromExecution - A type of render shot execution context supported by original schedule.
 * @typeParam TToExecution - A type of render shot execution context supported by mapped schedule.
 * @param schedule - A schedule to map from.
 * @param execute - Executes a render shot in mapped execution context.
 *
 * @returns Mapped render schedule instance.
 */
function mapRenderSchedule(schedule, execute) {
    return shot => schedule((fromExec) => {
        let toExec;
        const draft = {
            postpone(postponed) {
                fromExec.postpone(_exec => postponed(toExec));
            },
        };
        execute(fromExec, draft, exec => shot(toExec = exec));
    });
}

const RenderScheduleConfig = {
    /**
     * Constructs a configuration of render schedule by its options.
     *
     * @param options - Render scheduler options the configuration should be base on.
     *
     * @returns Render schedule configuration.
     */
    by(options = {}) {
        let win;
        const { error = console.error } = options;
        return {
            get node() {
                return options.node;
            },
            get window() {
                return win || (win = options.window || (options.node ? nodeWindow(options.node) : window));
            },
            error,
        };
    },
};

/**
 * Creates an abortable render schedule.
 *
 * @typeParam TExecution - A type of supported render shot execution context.
 * @typeParam TOptions - A type of accepted render schedule options.
 * @param scheduler - A render scheduler to schedule rendering by.
 * @param supply - Schedule supply. Once cut off, stops rendering in all schedules created by new scheduler.
 * A new supply will be created if omitted.
 *
 * @returns New abortable render scheduler instance.
 */
function newAbortableRenderScheduler(scheduler, supply = new Supply()) {
    const abortableScheduler = ((options) => {
        const scheduleSupply = (options === null || options === void 0 ? void 0 : options.supply) || new Supply();
        scheduleSupply.needs(supply);
        let execute = (exec, draft, shot) => shot({
            ...exec,
            ...draft,
            supply: scheduleSupply,
        });
        let schedule = mapRenderSchedule(scheduler(options), (exec, draft, shot) => execute(exec, draft, shot));
        const abortableSchedule = ((shot) => schedule(shot));
        abortableSchedule.supply = scheduleSupply;
        scheduleSupply.whenOff(reason => {
            execute = AbortableRenderSchedule$doNotExecute;
            schedule = AbortableRenderSchedule$abort(reason, options);
        });
        return abortableSchedule;
    });
    abortableScheduler.supply = supply;
    return abortableScheduler;
}
function AbortableRenderSchedule$abort(reason, options) {
    const { error } = RenderScheduleConfig.by(options);
    return _shot => {
        error('Rendering aborted', reason);
    };
}
function AbortableRenderSchedule$doNotExecute(_exec, _draft, _shot) {
    // Do not execute in aborted schedule
}

const RenderQ__symbol = ( /*#__PURE__*/Symbol('render-q'));
class RenderQ {
    constructor(q, ref) {
        this.q = q;
        this.schedule = this.doSchedule;
        this.ref = ref || [this, this];
        this.execute = q.recur ? this.execRecurring : this.execNonRecurring;
    }
    static by(queue, ref) {
        return queue[RenderQ__symbol]
            || (queue[RenderQ__symbol] = new RenderQ(queue, ref));
    }
    add(shot) {
        this.q.add(shot);
    }
    doSchedule(config) {
        // At most one execution at a time.
        this.schedule = RenderQ$doNotSchedule;
        const postponed = [];
        const execution = {
            get config() {
                return config;
            },
            postpone(shot) {
                postponed.unshift(shot);
            },
        };
        this.q.schedule(() => {
            const next = this.reset();
            const done = () => {
                // Activate next queue.
                this.ref[1] = this.ref[0];
                // Schedule postponed shots (in reverse order).
                postponed.forEach(shot => this.q.add(shot));
                // Recurrently postponed shots are executed immediately after their initiators.
                execution.postpone = shot => this.q.post(shot);
                // Execute postponed shots.
                this.exec(execution);
                next.resume();
            };
            next.suspend();
            this.execute(execution, done);
        });
    }
    exec(execution) {
        for (;;) {
            const shot = this.q.pull();
            if (!shot) {
                break;
            }
            shot(execution);
        }
    }
    execNonRecurring(execution, done) {
        this.exec(execution);
        done();
    }
    execRecurring(execution, done) {
        const execute = () => {
            this.exec(execution);
            if (!this.q.recur(execute)) {
                done();
            }
        };
        execute();
    }
    reset() {
        // Update next queue. Current queue remains active
        return this.ref[0] = RenderQ.by(this.q.reset(), this.ref);
    }
    suspend() {
        this.schedule = config => {
            // Remember execution to schedule.
            // It will be scheduled on resume.
            this.scheduled = config;
            // No need to remember more than one execution to schedule.
            this.schedule = RenderQ$doNotSchedule;
        };
    }
    resume() {
        if (this.scheduled) {
            // There is an execution to schedule.
            this.doSchedule(this.scheduled);
        }
        else {
            // Resume normal execution scheduling.
            this.schedule = this.doSchedule;
        }
    }
}
function RenderQ$doNotSchedule(_config) {
    // Do not schedule.
}
/**
 * Builds custom render scheduler.
 *
 * @param options - Render scheduler options.
 *
 * @returns New render scheduler.
 */
function customRenderScheduler(options) {
    return (scheduleOptions) => {
        const config = RenderScheduleConfig.by(scheduleOptions);
        const queueRef = RenderQ.by(options.newQueue(config)).ref;
        let enqueued = [];
        return (shot) => {
            const [lastQueue, , executed] = enqueued;
            const [nextQueue, activeQueue] = queueRef;
            let queue = lastQueue || activeQueue;
            if ((lastQueue === activeQueue && !executed) || lastQueue === nextQueue) {
                // Replace the shot in active queue, unless executed already.
                // Replace the shot in the next queue unconditionally.
                enqueued[1] = shot;
            }
            else {
                // Add to active queue if no shot executed in this schedule yet, or the queue recurrent.
                // Add to the next queue otherwise.
                const nextEnqueued = enqueued = [
                    queue = !executed || queue.q.recur ? activeQueue : nextQueue,
                    shot,
                ];
                queue.add((execution) => {
                    nextEnqueued[2] = true; // Switch to the next queue.
                    try {
                        nextEnqueued[1]({
                            get config() {
                                return config;
                            },
                            postpone(postponed) {
                                execution.postpone(postponed);
                            },
                        });
                    }
                    catch (e) {
                        config.error(e);
                    }
                });
            }
            queue.schedule(config);
        };
    };
}

const RenderQueue = {
    /**
     * Builds the default implementation of render queue.
     *
     * @param options - Render queue options.
     *
     * @returns New render queue.
     */
    by(options) {
        const { schedule, recur } = options;
        let { replace = RenderQueue$doNotReplace } = options;
        let scheduled = [];
        let executed = scheduled;
        let scheduleRecurrent;
        if (recur) {
            scheduleRecurrent = task => {
                if (!scheduled.length) {
                    // No recurrent shots.
                    // The upcoming shots are non-recurrent.
                    scheduled = executed;
                    return false;
                }
                executed = scheduled;
                scheduled = [];
                recur(task);
                return true;
            };
            const replaceQueue = replace;
            replace = next => {
                scheduled = [];
                replaceQueue(next);
            };
        }
        return {
            add(shot) {
                scheduled.push(shot);
            },
            post(shot) {
                scheduled.unshift(shot);
            },
            pull() {
                return executed.shift();
            },
            schedule,
            recur: scheduleRecurrent,
            reset() {
                const next = RenderQueue.by(options);
                replace(next);
                return next;
            },
        };
    },
};
function RenderQueue$doNotReplace(_replacement) {
    // Do not replace queue
}

/**
 * @internal
 */
const animationRenderQueues = ( /*#__PURE__*/new WeakMap());
/**
 * A render scheduler that executes scheduled render shots within animation frame.
 *
 * Utilizes [requestAnimationFrame()] function for that.
 *
 * The render shots scheduled by different schedules created for the same window are all executed in the same animation
 * frame. The {@link RenderExecution.postpone postponed} render shots are executed only after all scheduled ones
 * complete.
 *
 * [requestAnimationFrame()]: https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame
 */
const animationRenderScheduler = ( /*#__PURE__*/customRenderScheduler({
    newQueue({ window }) {
        const existing = animationRenderQueues.get(window);
        if (existing) {
            return existing;
        }
        const newQueue = RenderQueue.by({
            schedule: task => window.requestAnimationFrame(task),
            replace: replacement => animationRenderQueues.set(window, replacement),
        });
        animationRenderQueues.set(window, newQueue);
        return newQueue;
    },
}));

function asyncRenderQueue$schedule(task) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.resolve().then(task);
}
let asyncRenderQueue = ( /*#__PURE__*/RenderQueue.by({
    schedule: asyncRenderQueue$schedule,
    recur: asyncRenderQueue$schedule,
    replace: replacement => asyncRenderQueue = replacement,
}));
/**
 * A render scheduler that executes scheduled render shots asynchronously.
 *
 * Recurrent render shots are also executed asynchronously.
 */
const asyncRenderScheduler = ( /*#__PURE__*/customRenderScheduler({
    newQueue: () => asyncRenderQueue,
}));

/**
 * Context entry containing default window instance.
 *
 * Defaults to current window.
 */
const CxWindow = {
    perContext: ( /*#__PURE__*/cxSingle({ byDefault: () => window })),
    toString: () => '[CxWindow]',
};

/**
 * Creates a render scheduler that delegates scheduling to the given one.
 *
 * @typeParam TExecution - A type of supported render shot execution context.
 * @typeParam TOptions - A type of accepted render schedule options.
 * @param scheduler - A render scheduler to schedule rendering by.
 *
 * @returns New delegating render scheduler instance.
 */
function newDelegateRenderScheduler(scheduler) {
    const result = ((options) => {
        let usedScheduler = scheduler;
        let schedule = scheduler(options);
        return shot => {
            if (usedScheduler !== scheduler) {
                usedScheduler = scheduler;
                schedule = scheduler(options);
            }
            schedule(shot);
        };
    });
    result.scheduleBy = newScheduler => {
        scheduler = newScheduler;
        return result;
    };
    return result;
}

let defaultRenderScheduler = animationRenderScheduler;
/**
 * Assigns or resets the default render scheduler.
 *
 * An {@link animationRenderScheduler animation frame render scheduler} is used bu default.
 *
 * @param scheduler - New default render scheduler. {@link animationRenderScheduler animation frame render scheduler}
 * will be used if `null`, `undefined`, or omitted.
 *
 * @returns New default render scheduler.
 */
function setRenderScheduler(scheduler) {
    return defaultRenderScheduler = scheduler || animationRenderScheduler;
}
/**
 * Constructs {@link RenderSchedule render schedule} using {@link setRenderScheduler default render scheduler}.
 *
 * @param options - Options of constructed render schedule.
 *
 * @returns New render schedule.
 */
function newRenderSchedule(options) {
    return defaultRenderScheduler(options);
}

/**
 * @internal
 */
function noopRenderSchedule(_shot) {
    // Do not schedule
}

/**
 * A render scheduler that neither schedules, nor executes render shots.
 */
const noopRenderScheduler = _options => noopRenderSchedule;

/**
 * Creates context entry definer containing {@link RenderScheduler render scheduler}.
 *
 * Uses the most recently provided render scheduler. Sets {@link RenderSchedule.Options.window window} option for the
 * scheduler to {@link CxWindow contextual window} unless explicitly specified.
 *
 * @param byDefault - Creates render scheduler to use by default. {@link newRenderSchedule Default one} used when
 * omitted.
 *
 * @returns New context entry definer.
 */
function cxRenderScheduler({ byDefault = _ => newRenderSchedule, } = {}) {
    return cxRecent({
        create: (recent, _target) => recent,
        byDefault,
        assign({ track, to }, target) {
            const cxWindow = target.get(CxWindow);
            const delegate = newDelegateRenderScheduler(noopRenderScheduler);
            track(scheduler => delegate.scheduleBy(scheduler))
                .whenOff(reason => delegate.scheduleBy(cxUnavailable(target.entry, undefined, reason)));
            const scheduler = (options = {}) => {
                const { node, window = node ? nodeWindow(node) : cxWindow } = options;
                return delegate({
                    ...options,
                    window,
                });
            };
            return receiver => to((_, by) => receiver(scheduler, by));
        },
    });
}

/**
 * Render scheduler that executes render shots immediately.
 */
const immediateRenderScheduler = (options) => {
    const config = RenderScheduleConfig.by(options);
    return (shot) => {
        const postponed = [];
        const execution = {
            get config() {
                return config;
            },
            postpone(shot) {
                postponed.push(shot);
            },
        };
        execute(shot);
        for (;;) {
            const last = postponed.pop();
            if (!last) {
                break;
            }
            execute(last);
        }
        function execute(shot) {
            try {
                shot(execution);
            }
            catch (e) {
                config.error(e);
            }
        }
    };
};

/**
 * Creates new render scheduler that executes scheduled render shots on request.
 *
 * A {@link ManualRenderScheduler.render} method should be called to execute scheduled render shots.
 *
 * @returns New manual render scheduler.
 */
function newManualRenderScheduler() {
    const emptyTask = () => false;
    let pendingTask = emptyTask;
    let queue = RenderQueue.by({
        // Called at most once until reset
        schedule: task => pendingTask = () => {
            task();
            return true;
        },
        replace: replacement => {
            pendingTask = emptyTask;
            queue = replacement;
        },
    });
    const scheduler = customRenderScheduler({
        newQueue: () => queue,
    });
    scheduler.render = () => pendingTask();
    return scheduler;
}

/**
 * Context entry containing {@link PreRenderScheduler} instance.
 *
 * Uses {@link asyncRenderScheduler asynchronous} render scheduler by default.
 */
const PreRenderScheduler = {
    perContext: ( /*#__PURE__*/cxScoped(CxGlobals, ( /*#__PURE__*/cxRenderScheduler({ byDefault: _ => asyncRenderScheduler })))),
    toString: () => '[PreRenderScheduler]',
};

/**
 * @internal
 */
let immediateRenderQueue = ( /*#__PURE__*/RenderQueue.by({
    schedule: task => task(),
    replace: replacement => immediateRenderQueue = replacement,
}));
/**
 * A render scheduler that schedules render shots for immediate execution.
 *
 * In contrast to {@link immediateRenderScheduler} this one utilizes {@link RenderQueue render queue}. So it acts
 * similarly to other schedulers, such as {@link animationRenderScheduler}. In particular, it adds recurrent
 * render shots to render queue instead of executing them immediately.
 */
const queuedRenderScheduler = ( /*#__PURE__*/customRenderScheduler({
    newQueue: () => immediateRenderQueue,
}));

/**
 * Context entry containing {@link RenderScheduler} instance.
 *
 * This instance is globally singleton.
 *
 * Uses {@link newRenderSchedule default} render scheduler by default.
 */
const RenderScheduler = {
    perContext: ( /*#__PURE__*/cxScoped(CxGlobals, ( /*#__PURE__*/cxRenderScheduler()))),
    toString: () => '[RenderScheduler]',
};

export { CxWindow, PreRenderScheduler, RenderQueue, RenderScheduleConfig, RenderScheduler, animationRenderScheduler, asyncRenderScheduler, customRenderScheduler, cxRenderScheduler, immediateRenderScheduler, mapRenderSchedule, newAbortableRenderScheduler, newDelegateRenderScheduler, newManualRenderScheduler, newRenderSchedule, noopRenderScheduler, queuedRenderScheduler, setRenderScheduler };
//# sourceMappingURL=render-scheduler.js.map
